import React, { useCallback } from 'react';
import { Seo } from '../components/seo/Seo';
import { ContactForm, ContactFormInput } from '../components/forms/ContactForm';
import { NewsletterForm, NewsletterFormInput } from '../components/forms/NewsletterForm';
import { useUI, Notification as Toast } from '../context/UIContext';
import { ContactService } from '../service/ContactService';
import { NewsletterService } from '../service/NewsletterService';

// Response/error mapping preserved from the previous implementation; only the transport
// to the user changed (Bootstrap Alert → accessible toast via useUI().notify). Backend
// (ContactService / NewsletterService) is unchanged.
const UNKNOWN: Toast = { message: 'Unknown error. If it persists, please e-mail us directly.', variant: 'error' };

const mapError = (error: any): Toast => {
    const status = error?.response?.status;
    if (status === 400) return { message: 'Invalid form, please check your inputs.', variant: 'error' };
    if (status === 500) return { message: 'Something happened, please try again in a few moments.', variant: 'error' };
    return UNKNOWN;
};

export const ContactPage = () => {
    const { notify } = useUI();

    const onContactSubmit = useCallback(
        async (input: ContactFormInput) => {
            try {
                const res = await ContactService.sendMessage({ ...input });
                notify(res.status === 200 ? { message: 'Message Sent!', variant: 'success' } : UNKNOWN);
            } catch (error) {
                notify(mapError(error));
            }
        },
        [notify],
    );

    const onNewsletterSubmit = useCallback(
        async (input: NewsletterFormInput) => {
            try {
                const res = await NewsletterService.subscribe({ ...input });
                notify(res.status === 200 ? { message: 'Thank you for subscribing!', variant: 'success' } : UNKNOWN);
            } catch (error) {
                notify(mapError(error));
            }
        },
        [notify],
    );

    return (
        <section className="mx-auto max-w-forms">
            <Seo title="Contact" canonicalPath="/contact" description="Get in touch with Dragan Strukelj, or subscribe to the newsletter." />
            <div className="mb-6 text-eyebrow uppercase tracking-nav text-tone-800">Contact</div>
            <h1 className="sr-only">Contact</h1>

            <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_1px_1fr] lg:gap-12">
                <div>
                    <h2 className="text-meta uppercase tracking-nav text-ink">Contact Form</h2>
                    <p className="mt-2 text-caption text-tone-800">
                        If you have any questions please fill out the form below. Otherwise, e-mail us at{' '}
                        <a href="mailto:dragan@draganstrukelj.com" className="border-b border-tone-300">
                            dragan@draganstrukelj.com
                        </a>
                        .
                    </p>
                    <div className="mt-5">
                        <ContactForm onSubmit={onContactSubmit} />
                    </div>
                </div>

                {/* Divider: horizontal line when stacked (mobile), vertical between columns (desktop). */}
                <div aria-hidden className="h-px w-full bg-tone-150 lg:h-auto lg:w-px" />

                <div>
                    <h2 className="text-meta uppercase tracking-nav text-ink">Newsletter</h2>
                    <p className="mt-2 text-caption text-tone-800">
                        Subscribe and we’ll keep you informed about any new developments.
                    </p>
                    <div className="mt-5">
                        <NewsletterForm onSubmit={onNewsletterSubmit} />
                    </div>
                </div>
            </div>
        </section>
    );
};
